import {computed, DestroyRef, inject, Injectable, signal} from '@angular/core'
import {FrontEndMatchDto} from '@/api/player-api/models/front-end-match-dto'
import {BetslipCreateService} from '@/features/betslip/betslip-create/betslip-create.service'
import {DEMO_OFFER, OfferSelection} from './demo-offer'

export type OddsDirection = 'up' | 'down'

/** The last move of one outcome's odds, kept while it is being highlighted. */
export interface OddsMove {
	direction: OddsDirection
	from: number
	/**
	 * Counts every move. A CSS animation only restarts when its name changes, so the UI alternates
	 * two identical animations on this number's parity and a second move in a row re-runs the fade.
	 */
	seq: number
}

export const TICK_INTERVALS = [2000, 5000, 10000]

/**
 * How long a move stays "fresh": the offer shows the old price for this long, and the betslip's
 * 4s colour fade runs off the same window, so it must not be shorter than that fade.
 */
const HIGHLIGHT_MS = 4000

/** Each tick moves this many live outcomes. */
const MOVES_PER_TICK = {min: 1, max: 3}

/** A move is up to this share of the current odds, never below the feed's floor. */
const MAX_STEP = 0.08
const MIN_ODDS = 1.01

/**
 * Makes the demo offer's live fixtures move, the way the live feed does.
 *
 * Every tick picks a few outcomes on live matches and moves their odds by a few percent. The
 * offer updates, and every move that touches a Selection on the betslip reaches it the way the
 * feed does: `BetslipCreateService.updateSelections(FrontEndMatchDto[])`, which sets the
 * Selection's `oddsChangeFlag` and recalculates. Risk info is refetched because the odds are one
 * of its inputs.
 */
@Injectable()
export class LiveOddsTicker {

	private readonly betslip = inject(BetslipCreateService)

	/** The offer with its current odds. Starts from the demo's fixed prices. */
	readonly offer = signal<OfferSelection[]>(DEMO_OFFER.map((selection) => ({...selection})))

	readonly offerByMatch = computed(() => {
		const byMatch = new Map<number, OfferSelection[]>()
		for (const selection of this.offer()) {
			byMatch.set(selection.matchId, [...(byMatch.get(selection.matchId) ?? []), selection])
		}
		return Array.from(byMatch.values())
	})

	readonly moves = signal<ReadonlyMap<number, OddsMove>>(new Map())

	readonly running = signal(true)
	readonly intervalMs = signal(TICK_INTERVALS[1])
	readonly tickCount = signal(0)

	private timer: ReturnType<typeof setTimeout> | undefined
	private readonly highlightTimers = new Map<number, ReturnType<typeof setTimeout>>()
	private seq = 0

	constructor() {
		inject(DestroyRef).onDestroy(() => {
			clearTimeout(this.timer)
			this.highlightTimers.forEach((timer) => clearTimeout(timer))
		})
		this.schedule()
	}

	setRunning(running: boolean): void {
		this.running.set(running)
		this.schedule()
	}

	setInterval(ms: number): void {
		this.intervalMs.set(ms)
		this.schedule()
	}

	/** Moves some live odds now. */
	tick(): void {
		const live = this.offer().filter((selection) => selection.isLiveMatch)
		if (live.length === 0) {
			return
		}

		const count = MOVES_PER_TICK.min + Math.floor(Math.random() * (MOVES_PER_TICK.max - MOVES_PER_TICK.min + 1))
		const picked = new Set<number>()
		while (picked.size < Math.min(count, live.length)) {
			picked.add(live[Math.floor(Math.random() * live.length)].oddsId)
		}

		const moved: OfferSelection[] = []
		this.offer.update((offer) => offer.map((selection) => {
			if (!picked.has(selection.oddsId)) {
				return selection
			}

			const odds = nextOdds(selection.odds)
			if (odds === selection.odds) {
				return selection
			}

			this.highlight(selection.oddsId, {
				direction: odds > selection.odds ? 'up' : 'down',
				from: selection.odds,
				seq: ++this.seq
			})
			const next = {...selection, odds, oddsFormatted: odds.toFixed(2)}
			moved.push(next)
			return next
		}))

		this.tickCount.update((n) => n + 1)
		this.feedBetslip(moved)
	}

	private schedule(): void {
		clearTimeout(this.timer)
		if (!this.running()) {
			return
		}

		this.timer = setTimeout(() => {
			this.tick()
			this.schedule()
		}, this.intervalMs())
	}

	private highlight(oddsId: number, move: OddsMove): void {
		this.moves.update((moves) => new Map(moves).set(oddsId, move))

		clearTimeout(this.highlightTimers.get(oddsId))
		this.highlightTimers.set(oddsId, setTimeout(() => {
			this.highlightTimers.delete(oddsId)
			this.moves.update((moves) => {
				const next = new Map(moves)
				next.delete(oddsId)
				return next
			})
		}, HIGHLIGHT_MS))
	}

	/** Sends the moves on the betslip's Selections in the live feed's shape, one match at a time. */
	private feedBetslip(moved: OfferSelection[]): void {
		const onSlip = moved.filter((m) => this.betslip.isSelectionPresent(m.matchId, m.oddsId))
		if (onSlip.length === 0) {
			return
		}

		const matchIds = [...new Set(onSlip.map((m) => m.matchId))]
		const feMatches = matchIds.map((matchId) => ({
			matchId,
			matchOdds: [{
				markets: [{
					lines: [{
						s: 0,
						outcomes: onSlip
							.filter((m) => m.matchId === matchId)
							.map((m) => ({id: m.oddsId, v: m.odds, vf: m.oddsFormatted, s: 0}))
					}]
				}]
			}]
		}) as unknown as FrontEndMatchDto)

		this.betslip.updateSelections(feMatches)
	}
}

/** A few percent up or down, rounded to the cent, never below the floor. */
function nextOdds(odds: number): number {
	const step = (Math.random() * 2 - 1) * MAX_STEP
	return Math.max(MIN_ODDS, Math.round(odds * (1 + step) * 100) / 100)
}
